import { ReactElement } from 'react';
import { AnalyticsTag } from '@client/analytics/components/AnalyticsTag';
import { HOT_JAR_TAG } from '@client/analytics/types/types';
import { useCatalogPaginationFilters } from '@client/catalog/hooks';
import { ProductGroup } from '@client/common/components/ProductGroup';
import { RemoteRecord } from '@client/common/redux/RemoteRecord';
import { chunkArray } from '@client/common/utils/array/array';
import { useUserData } from '@client/context/hooks';
import { useTranslate } from '@client/i18n/hooks';
import { useProductsTiles } from '@client/product/hooks';
import { isProductGroupLoaded, PRODUCT_COUNT_IN_GROUP } from '@client/product/utils/product';
import { useCatalogSettingsData } from '@client/settings/hooks';
import { PRODUCT_BOX_STYLE_TYPE, ProductList } from '@lib/components/ProductList';
import classNames from 'classnames';
import { ProductType } from 'b2b-common/core/catalog/Catalog.types';
import { ProductTileType } from 'b2b-common/core/product/api/ProductTile.types';
import { Props } from './ResultsProductList.types';
import './ResultsProductList.scss';

export const ResultsProductList = ({
  products,
  priceWithVatLabel,
}: Props): ReactElement => {
  const { isLoggedIn } = useUserData();
  const { viewType } = useCatalogSettingsData();
  const { paginationFilters } = useCatalogPaginationFilters();
  const { page, pageSize } = paginationFilters;
  const productsCollection = useProductsTiles();
  const t = useTranslate();

  const tableHeaderText = {
    manufacturerSku: t('Manufacturer Product ID'),
    price: isLoggedIn
      ? t('Price<br />(excl. VAT)')
      : priceWithVatLabel || t('Price<br />(incl. VAT)'),
    sku: t('SKU'),
    stock: t('Stock'),
    title: t('Title'),
  };

  const rootClassName = classNames('ResultsProductList', {
    'Content–Scrollable': viewType === PRODUCT_BOX_STYLE_TYPE.table,
  });

  const areProductsLoaded = !Object.values(productsCollection)
    .filter((product) => product.wasStarted)
    .some((product) => product.isLoading);

  const getProductGroups = (
    products: ProductType[],
    productsCollection: Record<string, RemoteRecord<ProductTileType>>,
  ): {
    groupNumber: number;
    skus: string[];
  }[] => {
    let groupNumber = 0;
    return chunkArray(products, PRODUCT_COUNT_IN_GROUP).map(
      (productGroup: ProductType[]) => {
        const skus = productGroup.map((product) => product.sku);
        return {
          skus,
          groupNumber: !isProductGroupLoaded(skus, productsCollection)
            ? groupNumber++
            : groupNumber,
        };
      },
    );
  };

  const productGroups = getProductGroups(products, productsCollection);

  return (
    <div className={rootClassName}>
      <ProductList
        dataAttributes={{
          'data-testid': 'productList',
        }}
        styleType={viewType}
        text={tableHeaderText}
      >
        {productGroups.map((group, groupNumber) => (
          <ProductGroup
            skus={group.skus}
            groupNumber={group.groupNumber}
            absolutePosition={
              ((page - 1) * pageSize) + (groupNumber * PRODUCT_COUNT_IN_GROUP)
            }
            styleType={viewType}
            key={`${group.skus[0]}-${
              -((page - 1) * pageSize) + (groupNumber * PRODUCT_COUNT_IN_GROUP)
            }`}
            showDirectDelivery={true}
          />
        ))}
      </ProductList>

      {areProductsLoaded && (
        <AnalyticsTag tagName={HOT_JAR_TAG.catalogBottomReached} />
      )}
    </div>
  );
};
